import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../../ui-components/table/table";
import { TagColor } from "../../../../app/data/common/tagColor";
import XGSErrorMessage from "../../../../ui-components/error-message/errorMessage";
import Tag from "../../../../ui-components/molecules/tag/tag";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import LabeledDateRangeInput from "../../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import "./image-history-logs.scss";
import "../../podRecognition/podRecognition.scss";
import { ImageHistoryLogsFilterModel, ImageHistoryLogsFilterSchema, ImageHistoryLogsResponseItemModel, ImageHistoryLogsResponseModel } from "../../../../app/data/documents/imageHistoryLogsModels";
import { getImageHistoryLogs, imageHistoryLogsSelector } from "../../../../slices/documents/ImageHistoryLogsSlice";
import ReactViewer from "react-viewer";
import { ReactComponent as LinkIcon } from "../../../../images/link.svg";
import { DOCUMENT_TYPE_ACTIONS, getDocTypeTag, getIndexTag, LOCATION_ACTIONS, STATUS_ACTIONS } from "./filter-options";

const initialFilterValues: ImageHistoryLogsFilterModel = {
    docId: "",
    eventTimeFrom: "",
    eventTimeTo: "",
    trackNumber: "",
    customer: "",
    docStatus: undefined,
    terminal: undefined,
    docType: undefined,
    user: ""
};
const columnHelper = createColumnHelper<ImageHistoryLogsResponseItemModel>();
const columnChildHelper = createColumnHelper<ImageHistoryLogsResponseModel>();
interface ImageHistoryLogsProps {
    onShowRelatedEvents: (eventId: string) => void;
}
const ImageHistoryLogs: React.FC<ImageHistoryLogsProps> = ({ onShowRelatedEvents }) => {
    const imageHistoryLogsState = useSelector(imageHistoryLogsSelector);
    const filterFormRef = useRef<FormikProps<ImageHistoryLogsFilterModel>>(null);
    const dispatch = useDispatch();
    const [filter, setFilter] = useState<ImageHistoryLogsFilterModel>(initialFilterValues);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ top: number, left: number, width: number, height: number } | null>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [popupHeight, setPopupHeight] = useState<number>(0);
    const columnRef = useRef<HTMLDivElement | null>(null);
    const [lastPage, setLastPage] = useState<boolean>(false);
    const [showGallery, setShowGallery] = useState<boolean>(false);
    const [galleryImages, setGalleryImages] = useState<any>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
    const tableRef = React.useRef<HTMLElement>(null);

    const handleShowMore = (event: React.MouseEvent<HTMLDivElement>, rowContent: string | null, topMinus: number) => {
        const cell = event.currentTarget.getBoundingClientRect();
        setPopupPosition({
            top: cell.top + window.scrollY - topMinus,
            left: cell.left + window.scrollX - 216,
            width: cell.width,
            height: cell.height,
        });
        setExpandedRow(rowContent);
    };

    const handleClosePopup = () => {
        setExpandedRow(null);
        setPopupPosition(null);
    };

    const triggerFilterSearch = (eventIdValue: string) => {
        onShowRelatedEvents(eventIdValue);
    };
    const onSubmitFilter = (values: ImageHistoryLogsFilterModel) => {
        let filterValues = values;
        if (filterValues.eventTimeFrom) filterValues.eventTimeFrom = filterValues.eventTimeFrom.toApiDateFormat();
        if (filterValues.eventTimeTo) filterValues.eventTimeTo = filterValues.eventTimeTo.toApiDateFormat();
        setFilter(values);
        dispatch(getImageHistoryLogs(values, 0));
    };

    const onListInfiniteScroll = () => {
        if (imageHistoryLogsState.recordsRequest?.last === imageHistoryLogsState.recordsRequest?.page) return;
        dispatch(getImageHistoryLogs(filter, imageHistoryLogsState.recordsRequest ? imageHistoryLogsState.recordsRequest.page + 1 : 0));
    };

    const getLocationPerformedValues = () => {
        return Object.keys(LOCATION_ACTIONS).map((key) => {
            return {
                value: key,
                label: LOCATION_ACTIONS[key]
            };
        });
    };

    const getDocTypePerformedValues = () => {
        return Object.keys(DOCUMENT_TYPE_ACTIONS).map((key) => {
            return {
                value: key,
                label: DOCUMENT_TYPE_ACTIONS[key]
            };
        });
    };

    const getStatusPerformedValues = () => {
        return Object.keys(STATUS_ACTIONS).map((key) => {
            return {
                value: key,
                label: STATUS_ACTIONS[key]
            };
        });
    };

    useEffect(() => {
        if (imageHistoryLogsState.recordItems.length === 0)
            dispatch(getImageHistoryLogs(initialFilterValues, 0));
        // eslint-disable-next-line
    }, [dispatch]);

    useEffect(() => {
        if (imageHistoryLogsState.recordsRequest?.last === imageHistoryLogsState.recordsRequest?.page)
            setLastPage(true);
        else
            setLastPage(false);

        setGalleryImages(
            imageHistoryLogsState.recordItems.map((record: any, index: number) => {
                return {
                    src: record.image,
                    class: `xgs-image-history-logs__image-styles-${index}`,
                    rotation: 0,
                    flipHorizontal: false,
                    flipVertical: false,
                    zoomed: false,
                };
            })
        );
        // eslint-disable-next-line
    }, [imageHistoryLogsState.recordItems]);

    useEffect(() => {
        if (popupRef.current) {
            const height = popupRef.current.getBoundingClientRect().height;
            setPopupHeight(height);
        }
    }, [expandedRow, popupPosition]);

    useEffect(() => { // for closing the popup during scroll
        const tableElement = tableRef.current?.getElementsByClassName("xgs-table__fixed-size-list")?.[0];
        const handleScroll = () => {
            setExpandedRow(null);
        };

        const currentRef = tableElement;

        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
        // eslint-disable-next-line
    }, [tableRef.current?.getElementsByClassName("xgs-table__fixed-size-list")?.[0]]);

    const columns = [
        columnHelper.accessor("probill", {
            id: "probill",
            header: "Probill",
            cell: ({ row }) => (
                <>
                    {row.original.probill && row.original.probill !== '0' ? (
                        <>
                            {row.original.probill}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 130,
            minSize: 130
        }),
        columnHelper.accessor("failedeventsCount", {
            id: "failedeventsCount",
            header: "Failed Events Count",
            cell: (cellProps) => {
                return (
                    <Tag mods={{ color: cellProps.getValue().toString() === '0' ? TagColor.GREY : TagColor.RED, uppercase: true }}>
                        {cellProps.getValue()}
                    </Tag>
                );
            },
            size: 125,
            minSize: 125
        }),
        columnHelper.accessor("bol", {
            id: "bol",
            header: "BOL",
            cell: ({ row }) => (
                <>
                    {row.original.bol && row.original.bol !== '0' ? (
                        <>
                            {row.original.bol}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 130,
            minSize: 130
        }),
        columnHelper.accessor("po", {
            id: "po",
            header: "PO",
            cell: ({ row }) => (
                <>
                    {row.original.po && row.original.po !== '0' ? (
                        <>
                            {row.original.po}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 130,
            minSize: 130
        }),
        columnHelper.accessor("docId", {
            id: "docId",
            header: "Document ID",
            cell: ({ row }) => (
                <>
                    {row.original.docId && row.original.docId !== '0' ? (
                        <>
                            {row.original.docId}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 125,
            minSize: 125
        }),
        columnHelper.accessor("docType", {
            id: "docType",
            header: "Doc Type",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    getDocTypeTag(cellProps.getValue())
                );
            },
            size: 80,
            minSize: 50
        }),
        columnHelper.accessor("docStatus", {
            id: "docStatus",
            header: "Index Type",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    getIndexTag(cellProps.getValue())
                );
            },
            size: 100,
            minSize: 100
        }),
        columnHelper.accessor("terminalName", {
            id: "terminalName",
            header: "Terminal",
            cell: (cellProps) => {
                const terminal = cellProps.row.original.terminal;
                return (
                    <div>
                        <div>
                            {cellProps.getValue()}
                            <span className="text-light xgs-table__cell-subitem">
                                {terminal}
                            </span>
                        </div>
                    </div>
                );
            },
            size: 170,
            minSize: 170
        }),
        columnHelper.accessor("customerName", {
            id: "customerName",
            header: "customer",
            cell: (cellProps) => {
                const customerNumber = cellProps.row.original.customerNumber;
                return (
                    <div>
                        <div>
                            {cellProps.getValue()}
                            <span className="text-light xgs-table__cell-subitem">
                                {customerNumber && `${customerNumber}` ? (customerNumber && `${customerNumber}`) : (
                                    <strong>N/A</strong>
                                )}
                            </span>
                        </div>
                    </div>

                );
            },
            size: 210,
            minSize: 210
        }),
        columnHelper.accessor("image", {
            id: "image",
            header: "Document Link",
            cell: ({ row }) => (
                <>
                    {row.original.image ? (
                        <>
                            <div className="xgs-image-history-logs__image-container" onClick={() => { setCurrentImageIndex(row.index); setShowGallery(true); }}>
                                <div className="xgs-image-history-logs__icon-wrapper">
                                    <div className="xgs-image-history-logs__icon" onClick={() => { setCurrentImageIndex(row.index); setShowGallery(true); }}>
                                        <LinkIcon />
                                    </div>
                                </div>
                                <div className="xgs-image-history-logs__open-image-text">Open Image</div>
                            </div>
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 112,
            minSize: 112
        })
    ];


    const groupColumns = [
        columnChildHelper.accessor("eventTime", {
            id: "eventTime",
            header: "Time",
            cell: (cellProps) => {
                const rawValue = cellProps.getValue() as string | undefined;

                if (!rawValue) return null;

                const date = moment(rawValue, "YYYY-MM-DD HH:mm:ss.SSSSSSSSS");

                return (
                    <div>
                        <div>
                            {date.format("MM/DD/YY")}
                            <span className="text-light xgs-table__cell-subitem">
                                {date.format("hh:mm:ss A")}
                            </span>
                        </div>
                    </div>
                );
            },
            size: 170,
            minSize: 170
        }),
        columnChildHelper.accessor("imgStatus", {
            id: "imgStatus",
            header: "Status",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    <Tag mods={{ color: cellProps.getValue() === 'success' ? TagColor.GREEN : TagColor.RED, uppercase: true }}>
                        {cellProps.getValue()}
                    </Tag>
                );
            },
            size: 80,
            minSize: 80
        }),
        columnChildHelper.accessor("isPgmHalt", {
            id: "isPgmHalt",
            header: "Program Halt",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    <Tag mods={{ color: cellProps.getValue() === 'N' ? TagColor.GREEN : TagColor.RED, uppercase: true }}>
                        {cellProps.getValue()}
                    </Tag>
                );
            },
            size: 110,
            minSize: 110
        }),
        columnChildHelper.accessor("event", {
            id: "event",
            header: "Action",
            size: 120,
            minSize: 120
        }),
        columnChildHelper.accessor("eventSource", {
            id: "eventSource",
            header: "Source",
            size: 120,
            minSize: 120
        }),
        columnChildHelper.accessor("eventDetails", {
            id: "eventDetails",
            header: "Event Details",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                const showMoreVisible = value?.length ? value.length > 65 ? true : false : false;

                return (
                    <div ref={columnRef} className="xgs-image-history-logs__event-details-cell">
                        {showMoreVisible ? (
                            <>
                                {value?.substring(0, 65)}...
                                <div
                                    onMouseEnter={(e) => {
                                        if (showMoreVisible) {
                                            handleShowMore(e, value ? value : null, 30);
                                        }
                                    }}
                                    onMouseLeave={handleClosePopup}
                                    onClick={(e: any) => handleShowMore(e, value ? value : null, 42)}
                                    className="xgs-image-history-logs__show-more-less-btn"
                                >
                                    Show More
                                </div>
                            </>
                        ) : (
                            <span>{value}</span>
                        )}
                    </div>
                );
            },
            size: 545,
            minSize: 545
        }),
        columnChildHelper.accessor("associatedUser", {
            id: "associatedUser",
            header: "Associated User",
            cell: ({ row }) => (
                <>
                    {row.original.associatedUser ? (
                        <>
                            {row.original.associatedUser}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 150,
            minSize: 150
        }),
        columnChildHelper.accessor("associatedEmail", {
            id: "associatedEmail",
            cell: ({ row }) => (
                <>
                    {row.original.associatedEmail ? (
                        <>
                            {row.original.associatedEmail}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            header: "Associated Email",
            size: 150,
            minSize: 150
        }),
        columnChildHelper.accessor("eventId", {
            id: "eventId",
            header: "Event ID",
            cell: (cellProps) => (
                <span
                    className="xgs-table-link-cell"
                    onClick={(e) => {
                        e.stopPropagation();
                        triggerFilterSearch(cellProps.getValue());
                    }}
                >
                    {cellProps.getValue()}
                </span>
            ),
            size: 110,
            minSize: 110
        }),
    ];


    return (
        <div className="xgs-image-history-logs__content-primary">
            <Formik
                initialValues={initialFilterValues}
                onSubmit={onSubmitFilter}
                innerRef={filterFormRef}
                validationSchema={ImageHistoryLogsFilterSchema}
            >
                {(props: FormikProps<ImageHistoryLogsFilterModel>) => (
                    <Form>
                        <div className="xgs-image-history-logs">
                            <div className="xgs-image-history-logs__list__filters">
                                <div className="xgs-image-history-logs__controls__item">
                                    <XGSFormSelect
                                        name="terminal"
                                        label="Terminal:"
                                        labelMode={LabelModes.column}
                                        className="xgs-tracking-filter__input"
                                        options={getLocationPerformedValues()}
                                        isClearable
                                        controlled={true}
                                        value={props.values.terminal}
                                    />
                                </div>
                                <XGSFormInput
                                    type="text"
                                    name="docId"
                                    label="Document ID:"
                                    placeholder="Document ID"
                                    value={props.values.docId}
                                    onChange={(e) => props.setFieldValue("docId", e.currentTarget.value)}
                                    labelMode={LabelModes.column}
                                    className="xgs-image-history-logs__list__filters__input"
                                />
                                <XGSFormInput
                                    type="text"
                                    name="trackNumber"
                                    label="Tracking Number:"
                                    placeholder="Probill, BOL, PO"
                                    value={props.values.trackNumber}
                                    onChange={(e) => props.setFieldValue("trackNumber", e.currentTarget.value)}
                                    labelMode={LabelModes.column}
                                    className="xgs-image-history-logs__list__filters__input"
                                />
                                <XGSFormInput
                                    type="text"
                                    name="customer"
                                    label="Customer:"
                                    placeholder="Number, Name"
                                    value={props.values.customer}
                                    onChange={(e) => props.setFieldValue("customer", e.currentTarget.value)}
                                    labelMode={LabelModes.column}
                                    className="xgs-image-history-logs__list__filters__input"
                                />
                                <LabeledDateRangeInput
                                    label="Doc Added Date:"
                                    labelMode={LabelModes.column}
                                    className="xgs-image-history-logs__list__filters__dates"
                                    start={props.values.eventTimeFrom}
                                    end={props.values.eventTimeTo}
                                    maxDate={new Date(moment().format("MM/DD/YYYY"))}
                                    onStartChange={(v) => {
                                        props.setFieldValue("eventTimeFrom", v);
                                    }}
                                    onEndChange={(v) => {
                                        props.setFieldValue("eventTimeTo", v);
                                    }}
                                />
                                <div className="xgs-image-history-logs__controls__item">
                                    <XGSFormSelect
                                        name="docStatus"
                                        label="Document Status:"
                                        labelMode={LabelModes.column}
                                        className="xgs-tracking-filter__input"
                                        options={getStatusPerformedValues()}
                                        isClearable
                                        controlled={true}
                                        value={props.values.docStatus}
                                    />
                                </div>
                                <div className="xgs-image-history-logs__controls__item">
                                    <XGSFormSelect
                                        name="docType"
                                        label="Document Type:"
                                        labelMode={LabelModes.column}
                                        className="xgs-tracking-filter__input"
                                        options={getDocTypePerformedValues()}
                                        isClearable
                                        controlled={true}
                                        value={props.values.docType}
                                    />
                                </div>
                                <XGSFormInput
                                    type="text"
                                    name="user"
                                    label="User:"
                                    placeholder="Username, Email"
                                    value={props.values.user}
                                    onChange={(e) => props.setFieldValue("user", e.currentTarget.value)}
                                    labelMode={LabelModes.column}
                                    className="xgs-image-history-logs__list__filters__input"
                                />
                                <Button
                                    type="submit"
                                    theme={ButtonThemes.blue}
                                    className="xgs-image-history-logs__list__filters__button"
                                    spinner={imageHistoryLogsState.requestStarted && imageHistoryLogsState.requestCreator === "GET_RECORDS"}
                                    disabled={!props.isValid || imageHistoryLogsState.requestStarted}
                                >
                                    Search
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
            {(imageHistoryLogsState.requestFailed && (imageHistoryLogsState.requestCreator === "GET_RECORDS_PORTION" || imageHistoryLogsState.requestCreator === "GET_RECORDS")) && (
                <XGSErrorMessage>{imageHistoryLogsState.requestError}</XGSErrorMessage>
            )}
            {!(imageHistoryLogsState.requestFailed && imageHistoryLogsState.requestCreator === "GET_RECORDS") && (
                <div className="xgs-image-history-logs__list">
                    <Table
                        ref={tableRef}
                        isLoading={imageHistoryLogsState.requestStarted && imageHistoryLogsState.requestCreator === "GET_RECORDS"}
                        keepTableOnLoading
                        columns={columns}
                        sorting={true}
                        data={imageHistoryLogsState.recordItems}
                        rowHeight={56}
                        minTableHeight={240}
                        noResultsText="There are no entries"
                        infiniteScroll
                        infiniteScrollLoading={imageHistoryLogsState.requestStarted && imageHistoryLogsState.requestCreator === "GET_RECORDS_PORTION"}
                        infiniteScrollHasNext={!lastPage}
                        onInfiniteScroll={onListInfiniteScroll}
                        responsive
                        subRowsField="events"
                        subTableProps={{
                            columns: groupColumns,
                            sorting: true,
                        }}
                    />
                </div>
            )}
            {expandedRow !== null && popupPosition && (
                <div
                    ref={popupRef}
                    className="xgs-image-history-logs__event-details-popup"
                    style={{
                        position: "absolute",
                        top: popupPosition.top - popupHeight,
                        left: popupPosition.left,
                        width: popupPosition.width
                    }}
                    onMouseLeave={handleClosePopup}
                >
                    <div className="xgs-image-history-logs__popup-content">
                        <p>{expandedRow || "No details available"}</p>
                        <button
                            onClick={handleClosePopup}
                            className="xgs-image-history-logs__show-more-less-btn"
                        >
                            Show Less <i className="fas fa-minus"></i>
                        </button>
                    </div>
                </div>
            )}
            <ReactViewer
                visible={showGallery}
                onClose={() => setShowGallery(false)}
                images={galleryImages.map((image: any) => ({
                    src: image.src,
                    alt: "Gallery Image"
                }))}
                defaultScale={1.2}
                attribute={false}
                activeIndex={currentImageIndex}
            />
        </div>
    );

};

export default ImageHistoryLogs;